import type { DepartmentInfo, SaveDepartment } from '$lib/api/types';
import { moveWhere, type ListMove } from './order';
import { CUSTOM_DEPARTMENT_COLORS, DEPARTMENT_COLORS } from './palette';

export const BUILT_IN_DEPARTMENTS = [
	'gron',
	'kod',
	'mejeri',
	'brod',
	'kolonial',
	'frost',
	'andet'
] as const;

export type BuiltInDepartment = (typeof BUILT_IN_DEPARTMENTS)[number];

/** Where an item shows when the household doesn't know its department (deleted, or another household's own). */
export const FALLBACK_DEPARTMENT: BuiltInDepartment = 'andet';

/** A store department as the app shows it: built-in ones get their name and colour here, own ones bring theirs. */
export type DepartmentView = { key: string; name: string; color: string; custom: boolean };

/** The built-in departments' names; the message catalogue provides them. */
export type DepartmentNames = Record<BuiltInDepartment, string>;

export function isBuiltInDepartment(key: string): key is BuiltInDepartment {
	return (BUILT_IN_DEPARTMENTS as readonly string[]).includes(key);
}

export function departmentView(department: DepartmentInfo, names: DepartmentNames): DepartmentView {
	if (isBuiltInDepartment(department.key)) {
		const key = department.key;
		return { key, name: names[key], color: DEPARTMENT_COLORS[key], custom: false };
	}
	return {
		key: department.key,
		name: department.name ?? department.key,
		color: department.color ?? DEPARTMENT_COLORS[FALLBACK_DEPARTMENT],
		custom: true
	};
}

/**
 * The departments in their order, followed by the built-in ones they leave out: those always exist, also before the
 * household has loaded.
 */
export function departmentViews(
	departments: readonly DepartmentInfo[],
	names: DepartmentNames
): DepartmentView[] {
	const missing = BUILT_IN_DEPARTMENTS.filter((key) => !departments.some((d) => d.key === key));
	return [...departments, ...missing.map((key) => ({ key, name: null, color: null }))].map((d) =>
		departmentView(d, names)
	);
}

/** A department by key among `views` (from `departmentViews`); a key not among them shows as "Andet". */
export function departmentOf(views: readonly DepartmentView[], key: string): DepartmentView {
	return (
		views.find((d) => d.key === key) ?? views.find((d) => d.key === FALLBACK_DEPARTMENT) ?? views[0]
	);
}

/** A department being edited; a new own department has no key until it is saved. */
export type DepartmentDraft = { key: string | null; name: string | null; color: string | null };

export function toDrafts(views: readonly DepartmentView[]): DepartmentDraft[] {
	return views.map((d) =>
		d.custom
			? { key: d.key, name: d.name, color: d.color }
			: { key: d.key, name: null, color: null }
	);
}

/** What `PUT /api/household/departments` takes: built-in ones by key, own ones with their name and colour. */
export function toSaved(drafts: readonly DepartmentDraft[]): SaveDepartment[] {
	return drafts.map((d) =>
		d.key && isBuiltInDepartment(d.key)
			? { key: d.key }
			: { key: d.key, name: d.name, color: d.color }
	);
}

/** Whether `name` is already a department's name among `drafts` (built-in ones by their shown name). */
function nameTaken(
	drafts: readonly DepartmentDraft[],
	name: string,
	names: DepartmentNames
): boolean {
	const wanted = name.toLocaleLowerCase();
	return drafts.some(
		(draft) =>
			(draft.key && isBuiltInDepartment(draft.key) ? names[draft.key] : (draft.name ?? ''))
				.toLocaleLowerCase()
				.trim() === wanted
	);
}

/** The first palette colour no own department has yet; when all are taken, they are used again in turn. */
export function nextDepartmentColor(drafts: readonly DepartmentDraft[]): string {
	const used = new Set(drafts.map((d) => d.color?.toUpperCase()));
	const own = drafts.filter((d) => !d.key || !isBuiltInDepartment(d.key)).length;
	return (
		CUSTOM_DEPARTMENT_COLORS.find((color) => !used.has(color)) ??
		CUSTOM_DEPARTMENT_COLORS[own % CUSTOM_DEPARTMENT_COLORS.length]
	);
}

/** Adds an own department at the end in the first free colour; null when the name is empty or already a department's. */
export function addOwnDepartment(
	drafts: readonly DepartmentDraft[],
	name: string,
	names: DepartmentNames
): DepartmentDraft[] | null {
	const trimmed = name.trim();
	if (!trimmed || nameTaken(drafts, trimmed, names)) return null;
	return [...drafts, { key: null, name: trimmed, color: nextDepartmentColor(drafts) }];
}

/**
 * Renames or recolours an own department and keeps its key, so items and stores placed there stay; null when the
 * name is empty or another department's.
 */
export function changeOwnDepartment(
	drafts: readonly DepartmentDraft[],
	key: string,
	change: { name: string; color: string },
	names: DepartmentNames
): DepartmentDraft[] | null {
	const name = change.name.trim();
	const others = drafts.filter((d) => d.key !== key);
	if (!name || nameTaken(others, name, names)) return null;
	return drafts.map((d) => (d.key === key ? { key, name, color: change.color } : d));
}

/** A store's order: the departments it lists (known ones), then the rest in the household's order, as the server does. */
export function storeOrder(
	order: readonly string[],
	household: readonly DepartmentView[]
): DepartmentView[] {
	const listed = [...new Set(order)]
		.map((key) => household.find((d) => d.key === key))
		.filter((d): d is DepartmentView => d !== undefined);
	return [...listed, ...household.filter((d) => !listed.includes(d))];
}

/*
 * Moves go by key, not position: a queued move runs on the order the moves before it saved, where another department
 * may stand at the position that was clicked.
 */

/** The household's departments with the one with `key` moved; unchanged when it isn't there. */
export function moveDepartment(
	drafts: readonly DepartmentDraft[],
	key: string,
	move: ListMove
): DepartmentDraft[] {
	return moveWhere(drafts, (d) => d.key === key, move);
}

/** A store's order (as `storeOrder` shows it) with the department `key` moved, as keys to save. */
export function moveInStore(
	order: readonly string[],
	household: readonly DepartmentView[],
	key: string,
	move: ListMove
): string[] {
	const keys = storeOrder(order, household).map((d) => d.key);
	return moveWhere(keys, (k) => k === key, move);
}

/**
 * Whether a store follows the household's order: it has no order of its own, so it changes with the household's.
 * An own order that happens to match it still stays as it is.
 */
export const followsStandard = (order: readonly string[]): boolean => order.length === 0;
